import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaTimes, FaUserPlus } from 'react-icons/fa';
import countryOptions from '../../../../../Constants/countryOptions';
import { createAdminAccountAct } from '../../../../../Redux/Dashboard/External/AdminUsers/Actions/CreateAdminAccountAction';
import {
  ADMIN_CREATE_ACCOUNT_TYPE_OPTIONS,
  applyCreateAccountFieldChange,
  buildAdminCreateAccountPayload,
  deriveCreateAccountValues,
  getDefaultCreateAccountFormState,
  mapCreateAdminAccountError,
  PENDING_ROLE_OPTIONS,
  REGION_OPTIONS,
  validateCreateAccountForm,
} from '../accountCreationHelpers';

const CreateAccountModal = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const isCreating = useSelector((state) => state.AdminCreateUserAccount?.loading);

  const [formData, setFormData] = useState(getDefaultCreateAccountFormState);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const {
    requiresCompanyInput,
    isBmcCompanyType,
    finalCompanyValue,
  } = deriveCreateAccountValues(formData);

  const showCompanyInput = requiresCompanyInput;
  const showBusinessUnitOtherInput = formData.businessUnit === 'Other';
  const showCustomRoleInput = formData.role === 'Others';

  const resetForm = () => {
    setFormData(getDefaultCreateAccountFormState());
    setFieldErrors({});
    setSubmitError('');
  };

  const handleClose = () => {
    if (isCreating) {
      return;
    }

    resetForm();
    onClose();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => applyCreateAccountFieldChange(prev, name, value));

    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleManagerToggle = (value) => {
    setFormData((prev) => ({
      ...prev,
      isManager: value,
    }));
  };

  const getInputClassName = (field, extraClass = '') => {
    const classes = ['admin-users-create-form__control'];

    if (extraClass) {
      classes.push(extraClass);
    }

    if (fieldErrors[field]) {
      classes.push('is-invalid');
    }

    return classes.join(' ');
  };

  const renderFieldError = (field) => {
    if (!fieldErrors[field]) {
      return null;
    }

    return (
      <span className="admin-users-create-form__error" role="alert">
        {fieldErrors[field]}
      </span>
    );
  };

  const renderRequiredLabel = (label) => (
    <>
      {label}
      <span className="admin-users-create-form__required">*</span>
    </>
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isCreating) {
      return;
    }

    const validation = validateCreateAccountForm(formData);

    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      setSubmitError('');
      return;
    }

    setSubmitError('');
    setFieldErrors({});

    const payload = buildAdminCreateAccountPayload(formData);

    try {
      const response = await dispatch(createAdminAccountAct(payload));

      if (!response?.username || !response?.password) {
        const mapped = mapCreateAdminAccountError(
          'Unable to create the account. Please try again.'
        );
        setSubmitError(mapped.submitError);
        if (Object.keys(mapped.fieldErrors).length > 0) {
          setFieldErrors(mapped.fieldErrors);
        }
        return;
      }

      const credentials = {
        fullName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        email: formData.email.trim(),
        username: response.username,
        password: response.password,
      };

      resetForm();
      onSuccess(credentials);
    } catch (error) {
      const mapped = mapCreateAdminAccountError(error?.message);
      setSubmitError(mapped.submitError);
      if (Object.keys(mapped.fieldErrors).length > 0) {
        setFieldErrors((prev) => ({ ...prev, ...mapped.fieldErrors }));
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="admin-users-modal-overlay admin-users-create-overlay" onClick={handleClose}>
      <div
        className="admin-users-create-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-account-modal-title"
      >
        <form className="admin-users-create-form" onSubmit={handleSubmit}>
          <div className="admin-users-create-form__header">
            <div className="admin-users-create-form__header-main">
              <div className="admin-users-create-form__header-icon" aria-hidden="true">
                <FaUserPlus />
              </div>
              <div>
                <h2 className="admin-users-create-form__title" id="create-account-modal-title">
                  Create User Account
                </h2>
                <p className="admin-users-create-form__subtitle">
                  Create and configure a Tech Central account.
                </p>
              </div>
            </div>
            <button
              type="button"
              className="admin-users-create-form__close"
              onClick={handleClose}
              disabled={isCreating}
              aria-label="Close create account modal"
            >
              <FaTimes />
            </button>
          </div>

          <div className="admin-users-create-form__body">
            {submitError && (
              <div className="admin-users-create-form__submit-error" role="alert">
                {submitError}
              </div>
            )}

            <section className="admin-users-create-form__section">
              <h3 className="admin-users-create-form__section-title">Personal Information</h3>
              <div className="admin-users-create-form__grid admin-users-create-form__grid--personal">
                <div className="admin-users-create-form__field">
                  <label className="admin-users-create-form__label" htmlFor="create-first-name">
                    {renderRequiredLabel('First Name')}
                  </label>
                  <input
                    id="create-first-name"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={getInputClassName('firstName')}
                  />
                  {renderFieldError('firstName')}
                </div>

                <div className="admin-users-create-form__field">
                  <label className="admin-users-create-form__label" htmlFor="create-last-name">
                    {renderRequiredLabel('Last Name')}
                  </label>
                  <input
                    id="create-last-name"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={getInputClassName('lastName')}
                  />
                  {renderFieldError('lastName')}
                </div>

                <div className="admin-users-create-form__field admin-users-create-form__field--email">
                  <label className="admin-users-create-form__label" htmlFor="create-email">
                    {renderRequiredLabel('Email')}
                  </label>
                  <input
                    id="create-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={getInputClassName('email')}
                  />
                  {renderFieldError('email')}
                </div>
              </div>
            </section>

            <section className="admin-users-create-form__section">
              <h3 className="admin-users-create-form__section-title">Account &amp; Organization</h3>
              <div className="admin-users-create-form__grid admin-users-create-form__grid--account">
                <div className="admin-users-create-form__field">
                  <label className="admin-users-create-form__label" htmlFor="create-type-of-user">
                    {renderRequiredLabel('Type of User')}
                  </label>
                  <select
                    id="create-type-of-user"
                    name="typeOfUser"
                    value={formData.typeOfUser}
                    onChange={handleChange}
                    className={`${getInputClassName('typeOfUser')} admin-users-create-form__control--select`}
                  >
                    <option value="">Select</option>
                    {ADMIN_CREATE_ACCOUNT_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {renderFieldError('typeOfUser')}
                </div>

                <div className="admin-users-create-form__field">
                  <label className="admin-users-create-form__label" htmlFor="create-business-unit">
                    {renderRequiredLabel('Business Unit')}
                  </label>
                  <select
                    id="create-business-unit"
                    name="businessUnit"
                    value={formData.businessUnit}
                    onChange={handleChange}
                    className={`${getInputClassName('businessUnit')} admin-users-create-form__control--select`}
                  >
                    <option value="DSOM">DSOM</option>
                    <option value="Other">Other</option>
                  </select>
                  {renderFieldError('businessUnit')}
                </div>

                <div className="admin-users-create-form__field">
                  <label className="admin-users-create-form__label" htmlFor="create-role">
                    Role
                  </label>
                  <div className="admin-users-create-form__stack">
                    <select
                      id="create-role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="admin-users-create-form__control admin-users-create-form__control--select"
                    >
                      <option value="">Select</option>
                      {PENDING_ROLE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {showCustomRoleInput && (
                      <input
                        type="text"
                        name="customRole"
                        value={formData.customRole}
                        onChange={handleChange}
                        className={getInputClassName('customRole')}
                        placeholder="Enter custom role"
                      />
                    )}
                  </div>
                  {renderFieldError('customRole')}
                </div>

                <div className="admin-users-create-form__field">
                  <label className="admin-users-create-form__label" htmlFor="create-company">
                    {renderRequiredLabel('Company')}
                  </label>
                  {showCompanyInput ? (
                    <input
                      id="create-company"
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={getInputClassName('company')}
                      placeholder="Enter company name"
                    />
                  ) : (
                    <>
                      <input
                        id="create-company"
                        type="text"
                        value={isBmcCompanyType ? finalCompanyValue : formData.company}
                        className="admin-users-create-form__control is-disabled"
                        disabled
                        readOnly
                      />
                      {isBmcCompanyType && (
                        <span className="admin-users-create-form__hint">
                          Automatically determined by user type
                        </span>
                      )}
                    </>
                  )}
                  {renderFieldError('company')}
                </div>

                {showBusinessUnitOtherInput && (
                  <div className="admin-users-create-form__field admin-users-create-form__field--full">
                    <label className="admin-users-create-form__label" htmlFor="create-custom-business-unit">
                      {renderRequiredLabel('Add Business Unit')}
                    </label>
                    <input
                      id="create-custom-business-unit"
                      type="text"
                      name="customBusinessUnit"
                      value={formData.customBusinessUnit}
                      onChange={handleChange}
                      className="admin-users-create-form__control"
                      placeholder="Enter business unit"
                    />
                  </div>
                )}
              </div>
            </section>

            <section className="admin-users-create-form__section">
              <h3 className="admin-users-create-form__section-title">Location &amp; Management</h3>
              <div className="admin-users-create-form__grid">
                <div className="admin-users-create-form__field">
                  <label className="admin-users-create-form__label" htmlFor="create-country">
                    {renderRequiredLabel('Country')}
                  </label>
                  <select
                    id="create-country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={`${getInputClassName('country')} admin-users-create-form__control--select`}
                  >
                    <option value="">Select</option>
                    {countryOptions.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                  {renderFieldError('country')}
                </div>

                <div className="admin-users-create-form__field">
                  <label className="admin-users-create-form__label" htmlFor="create-region">
                    {renderRequiredLabel('Region')}
                  </label>
                  <select
                    id="create-region"
                    name="region"
                    value={formData.region}
                    onChange={handleChange}
                    className={`${getInputClassName('region')} admin-users-create-form__control--select`}
                  >
                    <option value="">Select</option>
                    {REGION_OPTIONS.map((region) => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </select>
                  {renderFieldError('region')}
                </div>

                <div className="admin-users-create-form__field">
                  <label className="admin-users-create-form__label" htmlFor="create-manager-name">
                    Manager Name
                  </label>
                  <input
                    id="create-manager-name"
                    type="text"
                    name="managerName"
                    value={formData.managerName}
                    onChange={handleChange}
                    className={getInputClassName('managerName')}
                  />
                  {renderFieldError('managerName')}
                </div>

                <div className="admin-users-create-form__field">
                  <span className="admin-users-create-form__label">Is Manager?</span>
                  <div className="admin-users-create-segment" role="group" aria-label="Is Manager?">
                    <button
                      type="button"
                      className={`admin-users-create-segment__button ${formData.isManager === 'Yes' ? 'is-active' : ''}`}
                      onClick={() => handleManagerToggle('Yes')}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`admin-users-create-segment__button ${formData.isManager === 'No' ? 'is-active' : ''}`}
                      onClick={() => handleManagerToggle('No')}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="admin-users-create-form__footer">
            <p className="admin-users-create-form__footer-note">
              Fields marked <span className="admin-users-create-form__required">*</span> are required
            </p>
            <div className="admin-users-create-form__footer-actions">
              <button
                type="button"
                className="admin-users-create-form__button admin-users-create-form__button--cancel"
                onClick={handleClose}
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="admin-users-create-form__button admin-users-create-form__button--primary"
                disabled={isCreating}
              >
                {isCreating ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAccountModal;
